import { Activity, AlertTriangle, ArrowRight, Bot, CircleDollarSign, Clock3, Radio, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLoading } from '../../components/Shells';
import { api, formatDate, statusLabel } from '../../lib/api';

export function AdminDashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.dashboard().then(setData).finally(() => setLoading(false)); }, []);
  if (loading) return <PageLoading label="彙整營運資料" />;
  if (!data) return <div className="inline-error">總覽資料載入失敗</div>;

  const active = (data.status.active || 0) + (data.status.expiring || 0);
  const cards = [
    { label: '有效會員', value: active, detail: `${data.status.expiring || 0} 位即將到期`, icon: Users, tone: 'dark' },
    { label: '本日新增', value: data.totals.new_today, detail: `累計 ${data.totals.users} 位會員`, icon: UserPlus, tone: 'light' },
    { label: '到期會員', value: data.status.expired || 0, detail: `${data.status.kicked || 0} 位已移出`, icon: Clock3, tone: 'warm' },
    { label: '匯款入帳', value: `NT$${Number(data.totals.revenue_twd||0).toLocaleString()}`, detail: `${data.totals.bank_pending||0} 筆匯款待審核`, icon: CircleDollarSign, tone: 'red' }
  ];
  const totalTrack = Math.max(1, data.totals.users);

  return (
    <div className="admin-page page-enter">
      {data.totals.demo_users > 0 && <div className="demo-bar">另有 {data.totals.demo_users} 位示範會員供操作預覽，不計入以下營運數字。</div>}
      <section className="metric-grid">
        {cards.map(({ label, value, detail, icon: Icon, tone }) => (
          <article className={`metric-card ${tone}`} key={label}><span><Icon size={20} /></span><p>{label}</p><strong>{value}</strong><small>{detail}</small></article>
        ))}
      </section>

      <Link className="bank-card bank-section-heading" to="/admin/bank"><span>匯款待審核：<strong>{data.totals.bank_pending||0}</strong> 筆</span><span>台幣入帳 NT${Number(data.totals.revenue_twd||0).toLocaleString()} <ArrowRight size={16}/></span></Link>
      <section className="admin-grid two-one">
        <article className="admin-panel member-health">
          <div className="panel-heading"><div><p>MEMBER HEALTH</p><h2>會員狀態分布</h2></div><Activity size={20} /></div>
          <div className="health-bar">
            <i className="active" style={{ width: `${active / totalTrack * 100}%` }} />
            <i className="pending" style={{ width: `${(data.status.pending || 0) / totalTrack * 100}%` }} />
            <i className="expired" style={{ width: `${((data.status.expired || 0) + (data.status.kicked || 0) + (data.status.banned || 0)) / totalTrack * 100}%` }} />
          </div>
          <div className="health-legend">
            <div><i className="active" /><span>有效</span><strong>{active}</strong></div>
            <div><i className="pending" /><span>待訂閱</span><strong>{data.status.pending || 0}</strong></div>
            <div><i className="expired" /><span>失效／移出</span><strong>{(data.status.expired || 0) + (data.status.kicked || 0) + (data.status.banned || 0)}</strong></div>
          </div>
          <Link className="panel-link" to="/admin/members">查看所有會員 <ArrowRight size={15} /></Link>
        </article>

        <article className="admin-panel connection-card">
          <div className="panel-heading"><div><p>SYSTEM STATUS</p><h2>群組連線</h2></div><Radio size={20} /></div>
          <div className={`connection-state ${data.group.chat_id ? 'ready' : ''}`}><span><Bot size={24} /></span><div><strong>{data.group.chat_id ? 'Bot 已連接' : '等待加入群組'}</strong><p>{data.group.group_title}</p></div></div>
          <dl><div><dt>Chat ID</dt><dd>{data.group.chat_id || '尚未取得'}</dd></div><div><dt>自動踢出</dt><dd>{data.group.auto_kick_expired ? '已啟用' : '已停用'}</dd></div><div><dt>歡迎訊息</dt><dd>{data.group.welcome_enabled ? '已啟用' : '已停用'}</dd></div></dl>
          <Link className="button secondary full" to="/admin/settings">檢查設定</Link>
        </article>
      </section>

      <section className="admin-grid equal">
        <article className="admin-panel">
          <div className="panel-heading"><div><p>EXPIRING SOON</p><h2>七日內到期</h2></div><span className="panel-count">{data.expiring.length}</span></div>
          <div className="compact-list">
            {data.expiring.length ? data.expiring.map((member: any) => (
              <Link to={`/admin/members?member=${member.id}`} key={member.id}><span className="list-avatar">{member.first_name?.slice(0, 1)}</span><div><strong>{member.last_name}{member.first_name}</strong><small>{member.plan_name || '自訂方案'}</small></div><time>{formatDate(member.expires_at)}</time></Link>
            )) : <div className="panel-empty"><ShieldCheck size={25} /><p>七日內沒有會員到期</p></div>}
          </div>
        </article>
        <article className="admin-panel">
          <div className="panel-heading"><div><p>LATEST ACTIVITY</p><h2>最近動態</h2></div><Link to="/admin/audit">全部紀錄</Link></div>
          <div className="activity-list">
            {data.recent.map((item: any) => <div key={item.id}><span className={item.action.includes('failed') ? 'danger' : ''}>{item.action.includes('failed') ? <AlertTriangle size={15} /> : <Activity size={15} />}</span><div><strong>{actionName(item.action)}</strong><small>{item.actor_name || item.actor_username || '系統自動執行'} · {formatDate(item.created_at, true)}</small></div></div>)}
          </div>
        </article>
      </section>
    </div>
  );
}

function actionName(action: string) {
  const names: Record<string, string> = {
    'bank.approved':'匯款已確認並開通', 'bank.reported':'會員已回報匯款', 'bank.needs_info':'匯款資料待補充', 'bank.created':'建立匯款訂單', 'bank.settings':'收款設定已更新', 'bank.cancelled':'匯款訂單已取消', 'bank.notification_retry':'重新傳送會員通知',
    'membership.grant': '會員期限已更新', 'telegram.member_joined': '新會員加入群組',
    'scheduler.grace_kick': '未訂閱會員已移出', 'settings.updated': '群組設定已更新',
    'payment.process_failed': '付款處理發生錯誤', 'member.ban': '會員已封鎖', 'member.kick': '會員已踢出'
  };
  return names[action] || action;
}
